/*
  Aromaperfuma motion engine.
  Attribute contract (all optional, all degrade to a still page):
    data-reveal="rise|fade|blur|zoom|mask|mask-side|stagger"  reveal once when scrolled into view
    data-split="words|chars|lines"                            split a heading and slide each piece up
    data-parallax="0.2"                                        drift at a speed relative to the scroll
    data-scroll-progress[="pin"] data-steps="3"                write --progress (0..1) and data-step while scrolling
    data-tilt                                                  write --tilt-x / --tilt-y (-1..1) from the pointer
    data-fit-text[="0.98"] data-fit-max="400"                  size one line of text to fill its box
    data-header                                                header show/hide and scrolled state
    data-vt-link / data-vt-media                               product image morph between pages
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var config = (window.Aroma && window.Aroma.config) || {};
  var motionConfig = config.motion || {};
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var motionOn = function () { return root.classList.contains('motion-on') && !reduceQuery.matches; };
  var isRtl = function () { return root.getAttribute('dir') === 'rtl'; };
  var arabic = /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/;

  function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }

  /* Reveal */
  var revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      reveal(entry.target);
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }) : null;

  function reveal(el) {
    el.classList.add('is-revealed');
    el.dispatchEvent(new CustomEvent('motion:revealed', { bubbles: true }));
  }

  function prepareStagger(el) {
    Array.prototype.forEach.call(el.children, function (child, index) {
      if (!child.style.getPropertyValue('--i')) child.style.setProperty('--i', index);
    });
  }

  /*
    Nothing on the first screen waits for JavaScript: the first section animates in with CSS
    alone, and anything already on screen when this runs simply stays visible. Only content
    that starts below the fold (or inside a closed dialog) is hidden, with the will-reveal
    class, and revealed as it scrolls in. Reads happen before writes to avoid layout thrash.
  */
  var firstSection = 'main > .shopify-section:first-child';

  function prepare(el) {
    if (el.dataset.revealDelay) el.style.setProperty('--reveal-delay', parseInt(el.dataset.revealDelay, 10) + 'ms');
    if (el.dataset.reveal === 'stagger') prepareStagger(el);
    if (el.hasAttribute('data-split')) splitText(el);
    el.classList.add('will-reveal');
    revealObserver.observe(el);
  }

  /* The first callback reports where everything is without forcing a layout: on screen now means
     stay visible; anywhere else means hide and reveal on the way in. */
  /* Content below the fold is prepared a few elements at a time in idle moments, so a long page
     never freezes the first screen's intro while its headings are split and hidden. */
  var prepareQueue = [];
  var prepareScheduled = false;
  var idle = window.requestIdleCallback || function (callback) { return setTimeout(function () { callback({ timeRemaining: function () { return 8; } }); }, 32); };
  function drainPrepareQueue(deadline) {
    prepareScheduled = false;
    var count = 0;
    while (prepareQueue.length && (count < 4 || deadline.timeRemaining() > 4)) {
      var el = prepareQueue.shift();
      if (el.isConnected && !el.classList.contains('is-revealed')) prepare(el);
      count++;
    }
    if (prepareQueue.length) schedulePrepare();
  }
  function schedulePrepare() {
    if (prepareScheduled) return;
    prepareScheduled = true;
    idle(drainPrepareQueue, { timeout: 400 });
  }
  var classifier = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      classifier.unobserve(entry.target);
      if (entry.isIntersecting && !entry.target._revealOnEntry) reveal(entry.target);
      else if (entry.target._revealOnEntry) prepare(entry.target);
      else prepareQueue.push(entry.target);
    });
    if (prepareQueue.length) schedulePrepare();
  }) : null;

  function initReveal(scope, animateVisible) {
    scope.querySelectorAll('[data-reveal], [data-split]').forEach(function (el) {
      if (el.dataset.motionReady) return;
      el.dataset.motionReady = 'true';
      if (!motionOn() || !revealObserver || !classifier) { reveal(el); return; }
      if (!animateVisible && el.closest(firstSection)) { reveal(el); return; }
      if (animateVisible) el._revealOnEntry = true;
      classifier.observe(el);
    });
  }

  /* Split text into masked words or letters */
  function splitText(el) {
    if (el.dataset.splitDone) return;
    var mode = el.dataset.split || 'words';
    var text = el.textContent.trim();
    if (!text || el.querySelector('a, button, input')) return;
    if (mode === 'chars' && arabic.test(text)) mode = 'words';
    el.dataset.splitDone = 'true';
    el.setAttribute('aria-label', text.replace(/\s+/g, ' '));
    var counter = { i: 0 };
    var nodes = Array.prototype.slice.call(el.childNodes);
    nodes.forEach(function (node) { splitNode(node, mode, counter); });
    /* Keep word order right for Latin text on Arabic pages (and the reverse) without
       changing the heading's own direction, which layout and alignment rely on. */
    var root = document.createElement('span');
    root.className = 'split-root';
    root.setAttribute('dir', arabic.test(text) ? 'rtl' : 'ltr');
    while (el.firstChild) root.appendChild(el.firstChild);
    el.appendChild(root);
    if (mode === 'lines') indexLines(el);
    if (mode === 'lines') {
      var timer;
      window.addEventListener('resize', function () {
        if (el.classList.contains('is-revealed')) return;
        clearTimeout(timer);
        timer = setTimeout(function () { indexLines(el); }, 150);
      });
    }
  }

  function splitNode(node, mode, counter) {
    if (node.nodeType === 3) {
      var parts = node.textContent.split(/(\s+)/);
      var fragment = document.createDocumentFragment();
      parts.forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { fragment.appendChild(document.createTextNode(' ')); return; }
        var line = document.createElement('span');
        line.className = 'split-line';
        line.setAttribute('aria-hidden', 'true');
        if (mode === 'chars') {
          Array.from(part).forEach(function (char) {
            var unit = document.createElement('span');
            unit.className = 'split-unit';
            unit.textContent = char;
            unit.style.setProperty('--i', counter.i++);
            line.appendChild(unit);
          });
        } else {
          var unit = document.createElement('span');
          unit.className = 'split-unit';
          unit.textContent = part;
          unit.style.setProperty('--i', counter.i++);
          line.appendChild(unit);
        }
        fragment.appendChild(line);
      });
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === 1 && node.tagName !== 'BR') {
      node.setAttribute('aria-hidden', 'true');
      Array.prototype.slice.call(node.childNodes).forEach(function (child) { splitNode(child, mode, counter); });
    }
  }

  function indexLines(el) {
    var lastTop = null;
    var line = -1;
    el.querySelectorAll('.split-unit').forEach(function (unit) {
      var top = Math.round(unit.parentNode.offsetTop);
      if (lastTop === null || Math.abs(top - lastTop) > 4) { line++; lastTop = top; }
      unit.style.setProperty('--i', line);
    });
  }

  /* Scroll-linked effects: parallax and progress */
  var scrollDriven = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
  var tracked = new Set();
  var visible = new Set();
  var ticking = false;
  var viewport = window.innerHeight;
  var visibilityObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) visible.add(entry.target);
      else visible.delete(entry.target);
    });
    requestTick();
  }, { rootMargin: '25% 0px 25% 0px' }) : null;

  function initScroll(scope) {
    scope.querySelectorAll('[data-parallax], [data-scroll-progress]').forEach(function (el) {
      if (tracked.has(el)) return;
      tracked.add(el);
      if (visibilityObserver) visibilityObserver.observe(el);
    });
    requestTick();
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function update() {
    ticking = false;
    var strength = motionOn() ? (typeof motionConfig.parallax === 'number' ? motionConfig.parallax : 0.6) : 0;
    var writes = [];
    /* Read everything first... */
    visible.forEach(function (el) {
      if (!el.isConnected) { visible.delete(el); tracked.delete(el); return; }
      var rect = el.getBoundingClientRect();
      var job = { el: el };
      if (el.hasAttribute('data-parallax')) {
        var speed = parseFloat(el.dataset.parallax) || 0.15;
        var base = el.parentElement ? el.parentElement.getBoundingClientRect() : rect;
        var offset = base.top + base.height / 2 - viewport / 2;
        job.parallax = (-offset * speed * strength).toFixed(2);
      }
      if (el.hasAttribute('data-scroll-progress')) {
        var progress;
        if (el.dataset.scrollProgress === 'pin') {
          var travel = rect.height - viewport;
          progress = travel > 0 ? clamp(-rect.top / travel, 0, 1) : clamp((viewport - rect.top) / (viewport + rect.height), 0, 1);
        } else {
          progress = clamp((viewport - rect.top) / (viewport + rect.height), 0, 1);
        }
        job.progress = progress;
      }
      writes.push(job);
    });
    /* ...then write, so the browser lays out once per frame */
    writes.forEach(function (job) {
      var el = job.el;
      if (job.parallax !== undefined && job.parallax !== el._aromaParallax) {
        el._aromaParallax = job.parallax;
        el.style.setProperty('--parallax-y', job.parallax);
      }
      if (job.progress !== undefined) {
        var value = job.progress.toFixed(4);
        /* A scene marked data-scroll-driven animates from the scroll in CSS where the browser can; writing --progress there would only cost a restyle every frame. */
        if (value !== el._aromaProgress && !(scrollDriven && el.hasAttribute('data-scroll-driven'))) {
          el._aromaProgress = value;
          el.style.setProperty('--progress', value);
        }
        var steps = parseInt(el.dataset.steps, 10);
        if (steps > 0) {
          var step = Math.min(steps - 1, Math.floor(job.progress * steps * 0.9999));
          if (String(step) !== el.dataset.step) {
            el.dataset.step = step;
            el.dispatchEvent(new CustomEvent('scene:step', { detail: { step: step, progress: job.progress }, bubbles: true }));
          }
        }
      }
    });
  }

  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', function () { viewport = window.innerHeight; requestTick(); });

  /* Pointer tilt */
  var tiltTargets = new Set();
  function initTilt(scope) {
    if (!fineQuery.matches) return;
    scope.querySelectorAll('[data-tilt]').forEach(function (el) {
      if (tiltTargets.has(el)) return;
      tiltTargets.add(el);
      var area = el.closest('section, .shopify-section') || el;
      var target = { x: 0, y: 0 };
      var current = { x: 0, y: 0 };
      var running = false;
      function loop() {
        current.x += (target.x - current.x) * 0.08;
        current.y += (target.y - current.y) * 0.08;
        el.style.setProperty('--tilt-x', current.x.toFixed(4));
        el.style.setProperty('--tilt-y', current.y.toFixed(4));
        if (Math.abs(target.x - current.x) > 0.001 || Math.abs(target.y - current.y) > 0.001) {
          window.requestAnimationFrame(loop);
        } else {
          running = false;
        }
      }
      function kick() { if (!running) { running = true; window.requestAnimationFrame(loop); } }
      area.addEventListener('pointermove', function (event) {
        if (!motionOn()) return;
        var rect = area.getBoundingClientRect();
        target.x = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
        target.y = clamp(((event.clientY - rect.top) / rect.height) * 2 - 1, -1, 1);
        kick();
      });
      area.addEventListener('pointerleave', function () { target.x = 0; target.y = 0; kick(); });
    });
  }

  /* Fit one line of display text to its container */
  var fitTargets = new Set();
  function fitText(el) {
    var box = el.parentElement;
    if (!box) return;
    var factor = parseFloat(el.dataset.fitText) || 1;
    var max = parseFloat(el.dataset.fitMax) || 1000;
    var min = parseFloat(el.dataset.fitMin) || 24;
    el.style.fontSize = '100px';
    el.style.whiteSpace = 'nowrap';
    var available = box.clientWidth - parseFloat(getComputedStyle(box).paddingLeft) - parseFloat(getComputedStyle(box).paddingRight);
    var width = el.scrollWidth;
    if (!width) return;
    var size = clamp((100 * available * factor) / width, min, max);
    el.style.fontSize = size.toFixed(2) + 'px';
    el.style.setProperty('--fit-size', size.toFixed(2) + 'px');
    el.classList.add('is-fitted');
  }
  function initFit(scope) {
    scope.querySelectorAll('[data-fit-text]').forEach(function (el) {
      fitTargets.add(el);
      fitText(el);
    });
  }
  var fitTimer;
  window.addEventListener('resize', function () {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(function () { fitTargets.forEach(function (el) { if (el.isConnected) fitText(el); else fitTargets.delete(el); }); }, 100);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { fitTargets.forEach(fitText); });
  }

  /* Header: hide on the way down, return on the way up */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header || header.dataset.motionReady) return;
    header.dataset.motionReady = 'true';
    var lastY = window.scrollY;
    var hideEnabled = motionConfig.headerHide !== false;
    var threshold = 12;
    var headerHeight = header.offsetHeight;
    var visibleValue = null;
    var pending = false;
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { headerHeight = header.offsetHeight; requestUpdate(); }).observe(header);
    }
    /* Runs at most once per frame, and only writes when something changed:
       a custom property on <html> restyles the whole page, so it must not be set on every scroll event. */
    function update() {
      pending = false;
      var y = window.scrollY;
      var delta = y - lastY;
      var scrolled = y > 24;
      if (scrolled !== header.classList.contains('is-scrolled')) header.classList.toggle('is-scrolled', scrolled);
      if (hideEnabled && !header.contains(document.activeElement) && !document.querySelector('dialog[open]')) {
        if (delta > threshold && y > headerHeight * 2) header.classList.add('is-hidden');
        else if (delta < -threshold || y < headerHeight) header.classList.remove('is-hidden');
      }
      if (Math.abs(delta) > threshold) lastY = y;
      var value = header.classList.contains('is-hidden') ? '0px' : headerHeight + 'px';
      if (value !== visibleValue) {
        visibleValue = value;
        root.style.setProperty('--header-visible', value);
      }
    }
    function requestUpdate() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener('scroll', requestUpdate, { passive: true });
    update();
  }

  /* Page transitions between product cards and product pages */
  function initViewTransitions() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-vt-link]');
      if (!link || !('onpagereveal' in window)) return;
      document.querySelectorAll('[style*="view-transition-name"]').forEach(function (el) { el.style.viewTransitionName = ''; });
      var scope = link.closest('[data-vt-scope]') || link;
      var media = scope.querySelector('[data-vt-media]');
      if (media) media.style.viewTransitionName = 'product-media';
    });

    window.addEventListener('pageswap', function (event) {
      if (!event.viewTransition) return;
      if (reduceQuery.matches) { event.viewTransition.skipTransition(); return; }
      var cancel = function () { try { event.viewTransition.skipTransition(); } catch (error) {} };
      ['pointerdown', 'keydown'].forEach(function (type) { window.addEventListener(type, cancel, { once: true }); });
    });

    window.addEventListener('pagereveal', function (event) {
      if (!event.viewTransition) return;
      if (reduceQuery.matches) { event.viewTransition.skipTransition(); return; }
      var from = window.navigation && window.navigation.activation && window.navigation.activation.from;
      var cameFromCard = from && from.url && sessionStorage.getItem('aroma:vt') === 'card';
      var target = document.querySelector('[data-vt-target]');
      if (target && cameFromCard) {
        target.style.viewTransitionName = 'product-media';
        event.viewTransition.finished.then(function () { target.style.viewTransitionName = ''; });
      }
      try { sessionStorage.removeItem('aroma:vt'); } catch (error) {}
      var cancel = function () { try { event.viewTransition.skipTransition(); } catch (error) {} };
      ['pointerdown', 'keydown'].forEach(function (type) { window.addEventListener(type, cancel, { once: true }); });
    });

    window.addEventListener('pageswap', function () {
      if (document.querySelector('[style*="product-media"]')) {
        try { sessionStorage.setItem('aroma:vt', 'card'); } catch (error) {}
      }
    });
  }

  /* Public API */
  function init(scope) {
    scope = scope || document;
    initReveal(scope);
    initScroll(scope);
    initTilt(scope);
    initFit(scope);
  }

  window.AromaMotion = {
    init: init,
    reveal: reveal,
    splitText: splitText,
    fitText: fitText,
    refresh: requestTick,
    enabled: motionOn
  };

  function boot() {
    init(document);
    initHeader();
    initViewTransitions();
    root.classList.add('motion-ready');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  reduceQuery.addEventListener('change', function () {
    if (reduceQuery.matches) {
      root.classList.remove('motion-on');
      document.querySelectorAll('[data-reveal], [data-split]').forEach(reveal);
    }
  });

  /* Theme editor: re-run on edited sections and show selected blocks straight away */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initHeader();
    event.target.querySelectorAll('[data-reveal], [data-split]').forEach(reveal);
  });
  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target;
    el.querySelectorAll('[data-reveal], [data-split]').forEach(reveal);
    if (el.matches('[data-reveal], [data-split]')) reveal(el);
  });
})();
